import { ReactNode } from "react";
import { cn } from "~/lib/utils";

interface SettingsGroupProps {
  // Names the group above the card. Omit for a card that needs no label.
  label?: string;
  // How many things are in the card, shown next to the label in a lighter
  // weight. Pass the length plainly: 0 draws nothing, since an empty card
  // already says so in its own body.
  count?: number;
  // A status chip beside the label. It sits on the left, and the header row
  // holds its height, so appearing and disappearing moves nothing.
  badge?: ReactNode;
  // Sits opposite the label — an "Add" button, a link. Rendered as given: an
  // action belongs to the card it acts on, so it carries its own weight.
  action?: ReactNode;
  // The card's contents are not currently in effect. Recedes the surface
  // without dimming what is on it, so the rows stay as readable as ever.
  inactive?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A card of related settings content, labeled from the outside.
 *
 * The label sits above the border instead of in a bar within it, so the card is
 * free to hold something that already has a header. Rows placed directly inside
 * are separated for you.
 *
 * State is carried by `badge` and `inactive` rather than by copy that comes and
 * goes: both are reserved space or pure surface, so nothing on the page shifts
 * when the state changes. `count` sits inside the label for the same reason —
 * it grows the heading rather than displacing the rows below it.
 */
export const SettingsGroup = ({
  label,
  count,
  badge,
  action,
  inactive = false,
  className,
  children,
}: SettingsGroupProps) => (
  <section className={cn("flex flex-col gap-2", className)}>
    {(label || badge || action) && (
      <div className="flex min-h-6 items-center justify-between gap-4 px-0.5">
        <div className="flex min-w-0 items-center gap-2">
          {label && (
            <h2
              className={cn(
                "truncate text-sm font-medium",
                inactive ? "text-muted-foreground" : "text-foreground"
              )}
            >
              {label}
              {count !== undefined && count > 0 && (
                <span className="ml-1 font-normal text-muted-foreground">({count})</span>
              )}
            </h2>
          )}
          {badge}
        </div>
        {action != null && <div className="shrink-0">{action}</div>}
      </div>
    )}
    <div
      className={cn(
        "divide-y divide-border overflow-hidden rounded-lg border border-border",
        inactive ? "bg-muted/40" : "bg-card"
      )}
    >
      {children}
    </div>
  </section>
);
